"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./IndexPageLayout.css";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  CssBaseline,
  ThemeProvider,
  useMediaQuery,
} from "@mui/material";
import { createHrErpTheme } from "../../theme/hrErpMuiTheme";
import {
  Menu as MenuIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  Moon,
  Sun,
  Bell,
  Search,
  ChevronsUpDown,
  Settings as SettingsLucide,
  Trash2,
} from "lucide-react";

import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useEmployeeDataStore from "../../Zustand/Store/useEmployeeDataStore";
import { routes } from "../../Configurations/PermissionConfigrations";
import NotAllowed from "../../Exceptions/NotAllowed";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import useInternDataStore from "../../Zustand/Store/useInternDataStore";
import useNotificationStore from "../../Zustand/Store/useNotificationStore";
import SideBar from "../../Assets/Static/SideBar";
import {
  applyPreferredOrganizationToUserPayload,
  clearClientPreferredOrganizationId,
  setClientPreferredOrganizationId,
} from "../../lib/clientActiveOrganization";
import {
  canViewOrganizationPage,
  hasPermissionAction,
} from "../../lib/organizationPermissions";
import {
  normalizeUserOrganizationSession,
  resolveOrganizationFromUserData,
  resolveOrganizationId,
  resolveOrganizationUserId,
} from "../../lib/organizationSession";
import logo from "../../Assets/Images/logo.png";

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString();
}

function initialsFromName(name) {
  const parts = String(name || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return (parts[0] || "U").slice(0, 2).toUpperCase();
}

/** Permission fetch lives in PermissionBootstrap (app-level). RouteProtector only waits. */
export default function RouteProtector({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("hrerp_dark_mode") === "true";
    } catch {
      return false;
    }
  });
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("hrerp-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const { resetData } = useEmployeeDataStore();
  const { resetData: clearData } = useInternDataStore();
  const {
    Permission,
    resetPermissions,
    isPermissionLoaded,
    permissionOrgUserId,
  } = usePermissionDataStore();

  const isMobile = useMediaQuery("(max-width:768px)", { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuContainerRef = useRef(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [orgQuery, setOrgQuery] = useState("");
  const [allClientOrgs, setAllClientOrgs] = useState([]);
  const [orgListLoading, setOrgListLoading] = useState(false);
  const [orgSwitching, setOrgSwitching] = useState(null);

  const {
    items: notificationItems,
    unreadCount,
    loading: notificationsLoading,
    loadUnreadCount,
    loadNotifications,
    markOneRead,
    markAllRead,
    removeOne,
  } = useNotificationStore();

  useEffect(() => {
    try {
      localStorage.setItem("hrerp_dark_mode", String(darkMode));
    } catch {
      // Ignore storage failures (private mode / blocked storage).
    }
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light"
    );
  }, [darkMode]);

  const theme = React.useMemo(
    () => createHrErpTheme(darkMode ? "dark" : "light"),
    [darkMode]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userData, setAccess, login } = useAuthStore();

  const activeOrganization = useMemo(
    () => resolveOrganizationFromUserData(userData),
    [userData]
  );
  const activeOrganizationId = resolveOrganizationId(userData);

  const closeMenu = () => setActiveMenu(null);

  const toggleMenu = (m) => {
    setActiveMenu((cur) => {
      const next = cur === m ? null : m;
      if (next === "org") {
        const clientId = activeOrganization?.client_id;
        const fallbackOrgs = Array.isArray(userData?.organizations)
          ? userData.organizations
          : Array.isArray(userData?.organization)
            ? userData.organization
            : activeOrganization
              ? [activeOrganization]
              : [];
        if (clientId) {
          setOrgListLoading(true);
          axios
            .get(`${MAIN_URL}/api/organizations/v1`, {
              params: { client_id: clientId },
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            })
            .then((res) =>
              setAllClientOrgs(res.data?.organizations?.data || fallbackOrgs)
            )
            .catch(() => setAllClientOrgs(fallbackOrgs))
            .finally(() => setOrgListLoading(false));
        } else {
          setAllClientOrgs(fallbackOrgs);
        }
        setOrgQuery("");
      }
      if (next === "notif" && activeOrganizationId) {
        loadUnreadCount(activeOrganizationId);
        loadNotifications(activeOrganizationId, "all");
      }
      return next;
    });
  };

  const switchOrg = async (orgId) => {
    if (orgSwitching) return;
    setOrgSwitching(orgId);
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/organizations/extra/active`,
        {
          organization_id: orgId,
          application_user_id: userData?.application_user_id,
        },
        {
          headers: getApiHeaders({
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        }
      );
      const { user } = response.data;
      const allOrgs = Array.isArray(user?.organization)
        ? user.organization
        : allClientOrgs;
      const activeOrg =
        allOrgs.find((o) => Number(o.organization_id) === Number(orgId)) ||
        allClientOrgs.find((o) => Number(o.organization_id) === Number(orgId)) ||
        null;
      setClientPreferredOrganizationId(orgId);
      const nextUser = normalizeUserOrganizationSession({
        ...user,
        organizations: allOrgs,
        organization: activeOrg,
      });
      login(nextUser);
      setAccess(
        nextUser?.role?.system_role_name ||
          nextUser?.role ||
          userData?.role ||
          "Admin"
      );
      closeMenu();
      navigate("/", { replace: true });
    } catch (e) {
      console.error("Org switch failed", e);
    } finally {
      setOrgSwitching(null);
    }
  };

  useEffect(() => {
    if (activeOrganizationId) {
      loadUnreadCount(activeOrganizationId);
    }
  }, [activeOrganizationId, loadUnreadCount]);

  useEffect(() => {
    if (!activeMenu) return;
    const isInsideOpenMenu = (target) => {
      if (!(target instanceof Element)) return false;
      if (target.closest(".ems-tb-pop")) return true;
      if (activeMenu === "user" && target.closest(".ems-user-chip")) return true;
      if (activeMenu === "notif" && target.closest(".ems-notif-btn")) return true;
      if (activeMenu === "org" && target.closest(".ems-company-chip")) return true;
      return false;
    };
    const onDown = (e) => {
      if (!isInsideOpenMenu(e.target)) setActiveMenu(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setActiveMenu(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [activeMenu]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed((c) => {
        const next = !c;
        try {
          localStorage.setItem("hrerp-collapsed", next ? "1" : "0");
        } catch {
          // Ignore storage failures.
        }
        return next;
      });
    }
  };

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const current = useAuthStore.getState().userData;
    if (!current) return;
    const org = current.organization;
    const alreadyNormalized =
      org && !Array.isArray(org) && org.organization_id != null;
    if (alreadyNormalized) return;
    login(normalizeUserOrganizationSession(current));
  }, [login]);

  const clearSessionAndGoToLogin = React.useCallback(() => {
    logout();
    resetPermissions();
    localStorage.removeItem("token");
    clearClientPreferredOrganizationId();
    navigate("/login", { replace: true });
  }, [logout, navigate, resetPermissions]);
  const access = userData?.role || "Admin";
  const [loadPermission, setLoadPermission] = useState(true);
  const activeOrganizationUserId = resolveOrganizationUserId(userData);

  const normalizedOrgUserId = useMemo(() => {
    if (
      activeOrganizationUserId == null ||
      activeOrganizationUserId === ""
    ) {
      return null;
    }
    const n = Number(activeOrganizationUserId);
    return Number.isFinite(n) ? n : null;
  }, [activeOrganizationUserId]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          logout();
          resetPermissions();
          clearClientPreferredOrganizationId();
          localStorage.removeItem("token");

          Swal.fire({
            title: "Logged out!",
            text: "You have been logged out.",
            icon: "success",
            confirmButtonColor: "#0891B2",
          });
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          Swal.fire({
            title: "Error",
            text: "Logout failed. Please try again.",
            icon: "error",
            confirmButtonColor: "#0891B2",
          });
        }
      }
    });
  };

  const [isAllowed, setisAllowed] = useState(true);
  // Safety timeout: if permissions never load (e.g. org user ID missing), stop
  // showing the "Checking Permissions…" spinner after 8 seconds.
  const [permCheckTimedOut, setPermCheckTimedOut] = useState(false);

  useEffect(() => {
    if (!loadPermission) {
      setPermCheckTimedOut(false);
      return;
    }
    const t = setTimeout(() => setPermCheckTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [loadPermission]);

  useEffect(() => {
    const currentPath = location.pathname;
    localStorage.setItem("lastVisitedRoute", currentPath);

    if (currentPath && Permission?.length > 0) {
      let basePath = currentPath;
      if (currentPath.includes("/edit")) {
        basePath = `${currentPath.split("/edit")[0]}/edit`;
      } else if (currentPath.includes("/view")) {
        basePath = `${currentPath.split("/view")[0]}/view`;
      }

      const normalizePath = (path) =>
        String(path || "")
          .trim()
          .replace(/\/+$/, "")
          .replace(/^(?!\/)/, "/");

      const normalizedCurrent = normalizePath(basePath);
      const matchedRoute = routes.find(
        (item) => normalizePath(item.name) === normalizedCurrent
      );

      if (!matchedRoute) {
        setisAllowed(true);
      } else if (
        matchedRoute.action &&
        hasPermissionAction(Permission, matchedRoute.action)
      ) {
        setisAllowed(true);
      } else {
        setisAllowed(false);
      }
    }
  }, [location, Permission]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      clearSessionAndGoToLogin();
      return;
    }

    const storeUser = useAuthStore.getState().userData;
    const alreadyHydrated =
      storeUser &&
      (storeUser.organizationuser?.organization_user_id ||
        (Array.isArray(storeUser.metadata) && storeUser.metadata.length > 0));

    if (alreadyHydrated) {
      return;
    }

    let cancelled = false;
    axios
      .get(`${MAIN_URL}/api/auth/token-login`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (cancelled) return;
        let { user } = response.data;
        user = applyPreferredOrganizationToUserPayload(user);
        setAccess(user?.role?.system_role_name);
        login(normalizeUserOrganizationSession(user));
      })
      .catch((err) => {
        if (cancelled) return;
        // Only force-logout on a definitive auth rejection (401/403).
        // Network errors, 5xx, or timeouts should NOT kill the session.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearSessionAndGoToLogin();
        } else {
          console.warn("token-login failed (non-auth error), keeping session:", err?.message);
        }
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      currentPath === "/organization/employee/employee-details/add" ||
      currentPath === "/organization/employee/employee-details"
    ) {
      resetData();
    }
  }, [location.pathname, resetData]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      currentPath === "/organization/intern/intern-details/add" ||
      currentPath === "/organization/intern/intern-details"
    ) {
      clearData();
    }
  }, [location.pathname, clearData]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadPermission(false);
      return;
    }

    if (!normalizedOrgUserId) {
      setLoadPermission(true);
      return;
    }

    const readyForUser =
      isPermissionLoaded &&
      Number(permissionOrgUserId) === normalizedOrgUserId;

    setLoadPermission(!readyForUser);
  }, [normalizedOrgUserId, isPermissionLoaded, permissionOrgUserId]);

  useEffect(() => {
    if (
      !normalizedOrgUserId ||
      !isPermissionLoaded ||
      Number(permissionOrgUserId) !== normalizedOrgUserId
    ) {
      return;
    }

    if (Array.isArray(Permission) && Permission.length === 0) {
      navigate("/not-authorised", { replace: true });
    }
  }, [
    Permission,
    normalizedOrgUserId,
    isPermissionLoaded,
    permissionOrgUserId,
    navigate,
  ]);

  if (loadPermission && !permCheckTimedOut) {
    return (
      <div className="ems-perm-loading">
        <div className="ems-perm-card">
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            style={{ animation: "spin 0.9s linear infinite" }}
          >
            <circle
              cx="18"
              cy="18"
              r="15"
              stroke="var(--ems-border-strong)"
              strokeWidth="3"
              fill="none"
            />
            <path
              d="M18 3 A15 15 0 0 1 33 18"
              stroke="var(--ems-primary)"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          <p className="ems-perm-title">Checking Permissions…</p>
          <p className="ems-perm-sub">Setting up your workspace</p>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname;

  if (access === "Admin" && currentPath === "/sidebar-option-page-1") {
    return <Navigate to="/" replace />;
  }

  if (!isAllowed) {
    return <NotAllowed />;
  }

  const canNavigateToOrganizationPage = canViewOrganizationPage(Permission);

  const handleSidebarExpand = () => {
    if (isMobile) {
      setMobileOpen(true);
      return;
    }
    setCollapsed(false);
    try {
      localStorage.setItem("hrerp-collapsed", "0");
    } catch {
      // Ignore storage failures.
    }
  };

  const renderSidebarContent = () => (
    <SideBar
      Permission={Permission}
      collapsed={collapsed && !isMobile}
      onRequestExpand={handleSidebarExpand}
    />
  );

  const roleLabel =
    typeof access === "string"
      ? access
      : access?.system_role_name || userData?.role?.system_role_name || "";

  const userInitials = initialsFromName(userData?.full_name);
  const filteredOrgs = allClientOrgs.filter(
    (o) =>
      !orgQuery ||
      (o.organization_name || "")
        .toLowerCase()
        .includes(orgQuery.toLowerCase())
  );

  const handleNotificationClick = (n) => {
    if (!n?.is_read) markOneRead(n.notification_id);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="ems-shell" ref={menuContainerRef}>
        {activeMenu && (
          <div
            className="ems-tb-pop-backdrop"
            aria-hidden="true"
            onPointerDown={(e) => {
              e.preventDefault();
              closeMenu();
            }}
          />
        )}
        <header className="ems-topbar">
          <div className="ems-topbar-lead">
            <button
              className="ems-icon-btn"
              onClick={handleDrawerToggle}
              aria-label="Toggle sidebar"
              type="button"
            >
              <MenuIcon style={{ fontSize: 21 }} />
            </button>
            <div className="ems-brand" onClick={() => navigate("/")}>
              <img src={logo} alt="HR ERP" className="ems-brand-mark" />
              <span className="ems-brand-name">HR ERP</span>
            </div>
          </div>

          <div className="ems-tb-anchor ems-org-switch">
            <button
              type="button"
              className={`ems-company-chip${activeMenu === "org" ? " on" : ""}`}
              onClick={() => toggleMenu("org")}
            >
              <span className="ems-company-name">
                {activeOrganization?.organization_name ||
                  userData?.organization?.organization_name ||
                  "Organization"}
              </span>
              <ChevronsUpDown size={16} />
            </button>
            {activeMenu === "org" && (
              <div className="ems-tb-pop ems-tb-pop-org">
                <div className="ems-tb-pop-head">
                  <span className="ems-tb-pop-title">Switch organization</span>
                </div>
                <div className="ems-tb-org-search">
                  <Search size={16} />
                  <input
                    placeholder="Search organizations…"
                    value={orgQuery}
                    onChange={(e) => setOrgQuery(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="ems-tb-org-list">
                  {orgListLoading ? (
                    <div className="ems-tb-empty">Loading…</div>
                  ) : filteredOrgs.length === 0 ? (
                    <div className="ems-tb-empty">No organizations found.</div>
                  ) : (
                    filteredOrgs.map((o) => {
                      const isActive =
                        String(o.organization_id) ===
                        String(activeOrganizationId);
                      const isSwitching = orgSwitching === o.organization_id;
                      const name = o.organization_name || "Organization";
                      const short = name
                        .split(" ")
                        .map((w) => w[0] || "")
                        .join("")
                        .toUpperCase()
                        .slice(0, 2);
                      return (
                        <button
                          key={o.organization_id}
                          type="button"
                          className={`ems-tb-org${isActive ? " active" : ""}`}
                          disabled={!!orgSwitching}
                          onClick={() => {
                            if (!isActive) switchOrg(o.organization_id);
                          }}
                        >
                          <span className="ems-tb-org-logo">{short}</span>
                          <span className="ems-tb-org-meta">
                            <span className="ems-tb-org-name">{name}</span>
                            {o.organization_short_name && (
                              <span className="ems-tb-org-short">
                                {o.organization_short_name}
                              </span>
                            )}
                          </span>
                          {isSwitching ? (
                            <span className="ems-tb-org-status">Switching…</span>
                          ) : isActive ? (
                            <span className="ems-tb-org-status active">
                              <span className="ems-tb-org-dot" />
                              Active
                            </span>
                          ) : (
                            <span className="ems-tb-org-status">Inactive</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="ems-tb-pop-foot">
                  <button
                    type="button"
                    className="ems-tb-foot-btn"
                    onClick={() => {
                      closeMenu();
                      if (canNavigateToOrganizationPage) {
                        navigate("/organization/details");
                      }
                    }}
                  >
                    Manage organizations
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="ems-topbar-spacer" />

          <div className="ems-topbar-tools">
            <button
              type="button"
              className="ems-icon-btn"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? "Light mode" : "Dark mode"}
            >
              {darkMode ? <Sun size={19} /> : <Moon size={19} />}
            </button>

            <div className="ems-tb-anchor">
              <button
                type="button"
                className={`ems-icon-btn ems-notif-btn${
                  activeMenu === "notif" ? " on" : ""
                }`}
                onClick={() => toggleMenu("notif")}
                aria-label="Notifications"
              >
                <Bell size={19} />
                {unreadCount > 0 && <span className="ems-notif-dot" />}
              </button>
              {activeMenu === "notif" && (
                <div className="ems-tb-pop ems-tb-pop-notif">
                  <div className="ems-tb-pop-head">
                    <span className="ems-tb-pop-title">
                      Notifications
                      {unreadCount > 0 && (
                        <span className="ems-tb-badge" style={{ marginLeft: 8 }}>
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="ems-tb-link"
                      disabled={unreadCount === 0}
                      onClick={() => markAllRead(null)}
                    >
                      Mark all read
                    </button>
                  </div>
                  {notificationsLoading ? (
                    <div className="ems-tb-empty">Loading…</div>
                  ) : notificationItems.length === 0 ? (
                    <div className="ems-tb-empty">No notifications yet.</div>
                  ) : (
                    <div className="ems-tb-notif-list">
                      {notificationItems.map((n) => (
                        <div
                          key={n.notification_id}
                          className={`ems-tb-notif${!n.is_read ? " unread" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleNotificationClick(n)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleNotificationClick(n);
                            }
                          }}
                        >
                          <div className="ems-tb-notif-ic">
                            <Bell size={16} />
                          </div>
                          <div className="ems-tb-notif-body">
                            <span className="ems-tb-notif-title">{n.title}</span>
                            {n.message && (
                              <span className="ems-tb-notif-text">
                                {n.message}
                              </span>
                            )}
                            <span className="ems-tb-notif-time">
                              {formatTimeAgo(n.created_at)}
                            </span>
                          </div>
                          <div className="ems-tb-notif-actions">
                            {!n.is_read && (
                              <span className="ems-tb-unread-dot" />
                            )}
                            <button
                              type="button"
                              className="ems-tb-notif-del"
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeOne(n.notification_id);
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="ems-icon-btn"
              title="Settings"
              onClick={() => {
                closeMenu();
                navigate("/organization/settings/new");
              }}
            >
              <SettingsLucide size={19} />
            </button>

            <div className="ems-tb-anchor">
              <button
                type="button"
                className={`ems-user-chip${activeMenu === "user" ? " on" : ""}`}
                onClick={() => toggleMenu("user")}
                aria-label="User menu"
              >
                <span className="ems-user-name">
                  {userData?.full_name || "User"}
                </span>
                <span className="ems-avatar">{userInitials}</span>
              </button>
              {activeMenu === "user" && (
                <div
                  className="ems-tb-pop ems-tb-pop-menu"
                  style={{ minWidth: 260 }}
                >
                  <div className="ems-tb-user-head">
                    <span
                      className="ems-avatar"
                      style={{ width: 44, height: 44, fontSize: 16 }}
                    >
                      {userInitials}
                    </span>
                    <div className="ems-tb-user-meta">
                      <span className="ems-tb-user-name">
                        {userData?.full_name || "User"}
                      </span>
                      <span className="ems-tb-user-mail">
                        {userData?.email || ""}
                      </span>
                      <span className="ems-tb-user-role">{roleLabel}</span>
                    </div>
                  </div>
                  <div className="ems-tb-divider" />
                  {access?.system_role_name === "Admin" && (
                    <button
                      type="button"
                      className="ems-tb-item"
                      onClick={() => {
                        closeMenu();
                        navigate("/my-profile");
                      }}
                    >
                      Profile
                    </button>
                  )}
                  {Permission &&
                    Permission.includes("USER_RESET_PASSWORD") && (
                      <button
                        type="button"
                        className="ems-tb-item"
                        onClick={() => {
                          closeMenu();
                          navigate("/settings/change-password");
                        }}
                      >
                        <LockIcon style={{ fontSize: 17 }} />
                        Change Password
                      </button>
                    )}
                  <div className="ems-tb-divider" />
                  <button
                    type="button"
                    className="ems-tb-item danger"
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                  >
                    <LogoutIcon style={{ fontSize: 17 }} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="ems-body">
          {isMobile && mobileOpen && (
            <div
              className="ems-sidebar-overlay"
              onClick={() => setMobileOpen(false)}
            />
          )}
          <aside
            className={[
              "ems-sidebar",
              collapsed && !isMobile ? "ems-collapsed" : "",
              isMobile && mobileOpen ? "mobile-open" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {renderSidebarContent()}
          </aside>
          <main className="ems-content">
            <div className="ems-content-inner">{children}</div>
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
